import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Shield, CheckCircle, Play } from 'lucide-react';
import { RTL_LANGUAGES } from '../../i18n';
import { useRotatingImage } from '../../hooks/useRotatingImage';
import { ALL_PHOTOS } from './photoPool';

export default function Hero() {
  const { t, i18n } = useTranslation('marketing');
  const isRTL = RTL_LANGUAGES.includes(i18n.language);
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
  const { currentSrc, isFading } = useRotatingImage(ALL_PHOTOS, 150000);

  return (
    <section className="relative min-h-[100vh] flex items-center overflow-hidden">
      {/* Bright warm gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 via-sky-50 to-emerald-50" />
        {/* Warm soft blobs */}
        <div className="absolute top-20 start-10 w-80 h-80 rounded-full bg-cyan-200/30 blur-[100px]" />
        <div className="absolute bottom-20 end-10 w-96 h-96 rounded-full bg-emerald-200/25 blur-[120px]" />
        <div className="absolute top-1/2 start-1/3 w-64 h-64 rounded-full bg-amber-100/20 blur-[80px]" />
        {/* Subtle dot grid */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #0891b2 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-36 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur-sm border border-cyan-200/50 text-cyan-700 text-xs font-semibold tracking-wider uppercase mb-8 shadow-sm"
            >
              <Shield className="w-3.5 h-3.5" />
              KipiKid Platform
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            </motion.div>

            {/* Title - two lines */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 leading-[1.1] mb-6 tracking-tight">
              {t('hero.title_line1')}
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-l from-cyan-600 to-teal-600">
                {t('hero.title_line2')}
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-10 max-w-xl">
              {t('hero.subtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-12">
              <a
                href="#overview"
                className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-cyan-600 text-white font-bold rounded-2xl text-base hover:bg-cyan-700 transition-all shadow-lg shadow-cyan-600/20 hover:shadow-xl hover:shadow-cyan-600/30 hover:-translate-y-0.5"
              >
                <Play className="w-4 h-4" />
                {t('hero.cta_learn')}
              </a>
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-2xl text-base hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              >
                {t('hero.cta_demo')}
                <ArrowIcon className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>SOC 2 Compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>256-bit Encryption</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>99.9% Uptime</span>
              </div>
            </div>
          </motion.div>

          {/* Image side - clean, no floating cards */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -40 : 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            {/* Main image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-cyan-600/10 ring-1 ring-black/5">
              <img
                src={currentSrc}
                alt=""
                className="w-full h-[320px] sm:h-[400px] lg:h-[440px] object-cover"
                style={{ opacity: isFading ? 0 : 1, transition: 'opacity 0.6s ease-in-out', objectPosition: '50% 30%' }}
                aria-hidden="true"
              />
              {/* Light warm overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-white/20 via-transparent to-cyan-50/10" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
